/**
 * TeamChatWebhooksCard — Settings panel for Slack/Discord webhook broadcasts.
 *
 * Lets the user paste a Slack and/or Discord webhook URL, toggle the feature
 * on/off, fire a one-tap test broadcast, and inspect the most recent
 * deliveries. When a deal is marked WON anywhere in the app, the backend
 * fires a styled message to every configured channel automatically.
 */
import { useEffect, useState } from "react";
import { Hash, Send, Power, Check, Loader2, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function TeamChatWebhooksCard() {
  const [data, setData] = useState(null);
  const [slack, setSlack] = useState("");
  const [discord, setDiscord] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [log, setLog] = useState([]);

  const refresh = async () => {
    try {
      const r = await api.get("/team-chat/settings");
      const l = await api.get("/team-chat/log?limit=5");
      setData(r.data);
      setLog(l.data?.broadcasts || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [r, l] = await Promise.all([
          api.get("/team-chat/settings"),
          api.get("/team-chat/log?limit=5"),
        ]);
        if (!alive) return;
        setData(r.data);
        setLog(l.data?.broadcasts || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (slack !== "") payload.slack_url = slack.trim();
      if (discord !== "") payload.discord_url = discord.trim();
      const r = await api.put("/team-chat/settings", payload);
      setData(r.data);
      setSlack(""); setDiscord("");
      toast.success("Saved. New wins will auto-post to your team.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save webhook");
    } finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.put("/team-chat/settings", { enabled: !data?.enabled });
      setData(r.data);
      toast.success(r.data?.enabled ? "Broadcasts enabled" : "Broadcasts paused");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/team-chat/test");
      const sent = r.data?.sent || 0;
      if (sent === 0) {
        toast.error("No webhooks configured — paste a URL first.");
      } else {
        toast.success(`Test broadcast sent (${sent} channel${sent === 1 ? "" : "s"}).`);
        refresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const hasAny = data?.has_slack || data?.has_discord;
  const digestOn = data?.daily_digest !== false;

  const toggleDigest = async () => {
    setBusy(true);
    try {
      const r = await api.put("/team-chat/settings", { daily_digest: !digestOn });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't toggle digest");
    } finally { setBusy(false); }
  };

  const testDigest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/team-chat/test-digest");
      const sent = r.data?.sent || 0;
      if (sent === 0) {
        toast.error("No webhooks configured (or digest paused).");
      } else {
        toast.success(`Digest preview posted (${sent} channel${sent === 1 ? "" : "s"}).`);
        const l = await api.get("/team-chat/log?limit=5");
        setLog(l.data?.broadcasts || []);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="card" data-testid="team-chat-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Hash size={11} /> Team chat broadcasts
          </div>
          <h3 className="font-display font-bold text-base mt-1">
            Auto-post wins to Slack &amp; Discord
          </h3>
          <p className="text-xs text-[var(--ink-3)] mt-1 max-w-xl">
            When you mark a deal <strong>WON</strong> on the dispo timeline, we&apos;ll
            fire a styled brag card to every channel below. Every closer on the team gets pinged in real time.
          </p>
        </div>
        {hasAny && (
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className={`btn-outline text-xs ${data?.enabled ? "" : "opacity-60"}`}
            data-testid="team-chat-toggle"
          >
            <Power size={12} /> {data?.enabled ? "Enabled" : "Paused"}
          </button>
        )}
      </div>

      <div className="space-y-3 mt-4">
        <WebhookRow
          label="Slack"
          icon={MessageSquare}
          accent="#4A154B"
          placeholder="https://hooks.slack.com/services/T00/B00/…"
          docsLabel="Get one in Slack"
          docsHref="https://api.slack.com/messaging/webhooks"
          value={slack}
          onChange={setSlack}
          existing={data?.has_slack}
          preview={data?.slack_url_preview}
          testid="team-chat-slack"
        />
        <WebhookRow
          label="Discord"
          icon={MessageSquare}
          accent="#5865F2"
          placeholder="https://discord.com/api/webhooks/123/abc…"
          docsLabel="Get one in Discord"
          docsHref="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
          value={discord}
          onChange={setDiscord}
          existing={data?.has_discord}
          preview={data?.discord_url_preview}
          testid="team-chat-discord"
        />
      </div>

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={busy || (!slack && !discord)}
          className="btn-primary text-sm"
          data-testid="team-chat-save"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
        </button>
        {hasAny && (
          <button
            type="button"
            onClick={test}
            disabled={testing}
            className="btn-outline text-sm"
            data-testid="team-chat-test"
          >
            {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Fire a test broadcast
          </button>
        )}
      </div>

      {hasAny && (
        <div
          className="mt-4 pt-4 border-t border-[var(--line)] flex items-start justify-between gap-3 flex-wrap"
          data-testid="team-chat-digest-row"
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5">
              ☀️ Daily deal-flow digest
            </div>
            <p className="text-xs text-[var(--ink-3)] mt-1 max-w-md">
              Every morning, post a compact summary card to your team channel:
              new GO/MAYBE deal count, hottest buyer, active streaks.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleDigest}
              disabled={busy}
              className={`btn-outline text-xs ${digestOn ? "" : "opacity-60"}`}
              data-testid="team-chat-digest-toggle"
            >
              <Power size={12} /> {digestOn ? "On" : "Off"}
            </button>
            <button
              type="button"
              onClick={testDigest}
              disabled={testing || !digestOn}
              className="btn-outline text-xs"
              data-testid="team-chat-digest-test"
            >
              {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Preview
            </button>
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)] font-bold mb-2">
            Recent broadcasts
          </div>
          <div className="space-y-1">
            {log.map((row, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-xs gap-2"
                data-testid="team-chat-log-row"
              >
                <span className="text-[var(--ink-2)] truncate">
                  🏆 {row.agent_name} · <span className="text-[var(--ink-3)]">{row.address}</span>
                </span>
                <span className="text-[var(--ink-3)] whitespace-nowrap flex items-center gap-1">
                  {row.slack_sent && <Badge label="Slack" />}
                  {row.discord_sent && <Badge label="Discord" />}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WebhookRow({ label, icon: Icon, accent, placeholder, docsLabel, docsHref,
                      value, onChange, existing, preview, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-white"
        style={{ background: accent }}
      >
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-sm font-semibold">{label}</span>
          <a
            href={docsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-[var(--ink-3)] hover:text-emerald-600 underline"
          >
            {docsLabel} →
          </a>
        </div>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="input w-full text-sm font-mono"
          data-testid={`${testid}-input`}
        />
        {existing && (
          <div className="text-[11px] text-emerald-600 mt-1.5 flex items-center gap-1">
            <Check size={11} /> Saved: <span className="font-mono text-[var(--ink-3)] truncate">{preview}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Badge({ label }) {
  return (
    <span className="inline-block px-1.5 py-0.5 rounded text-[9px] uppercase font-bold bg-emerald-100 text-emerald-700">
      {label}
    </span>
  );
}
